'use client';

import { cn } from '@lib/util/cn';

import { Spinner } from '@modules/common/icons';
import React from 'react';

const BASE =
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-bold text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-surface-interactive-default focus-visible:ring-offset-2 cursor-pointer disabled:cursor-not-allowed [&_svg]:size-5 [&_svg]:shrink-0 shrink-0';

const variantClasses = {
  primary:
    'bg-surface-interactive-default text-fg-neutral-strong hover:bg-surface-interactive-soft active:brightness-90 disabled:bg-brand-neutral-300 disabled:text-brand-neutral-500',
  secondary:
    'border-2 border-fg-neutral-strong bg-transparent text-fg-neutral-strong hover:bg-surface-neutral-subtle disabled:opacity-40',
  ghost:
    'bg-transparent text-fg-neutral-strong hover:bg-surface-neutral-subtle disabled:opacity-40',
} as const;

const sizeClasses = {
  sm: 'h-9 px-3 text-xs',
  md: 'h-11 px-4',
  lg: 'h-[45px] px-6 text-base',
} as const;

export type ButtonVariant = keyof typeof variantClasses;
export type ButtonSize = keyof typeof sizeClasses;

export type ButtonProps = React.ComponentProps<'button'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  asChild?: boolean;
};

function Button({
  className,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  asChild = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || isLoading;
  const resolvedClass = cn(
    BASE,
    variantClasses[variant],
    sizeClasses[size],
    className,
  );
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<Record<string, unknown>>;
    return React.cloneElement(child, {
      ...props,
      className: cn(resolvedClass, child.props.className as string | undefined),
      'aria-disabled': isDisabled ? 'true' : undefined,
      'aria-busy': isLoading ? 'true' : undefined,
    });
  }

  return (
    <button
      disabled={isDisabled}
      aria-disabled={isDisabled ? 'true' : undefined}
      aria-busy={isLoading ? 'true' : undefined}
      className={resolvedClass}
      {...props}
    >
      <span className={isLoading ? 'invisible' : undefined}>{children}</span>
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size="20" aria-hidden="true" />
        </span>
      )}
    </button>
  );
}

export { Button };
